import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Sparkles, X, Volume2, VolumeX, RotateCcw, Heart, Flame } from 'lucide-react';
import { useCelebration } from '../../contexts/CelebrationContext';

// Native Web Audio Synthesizer (No external audio files needed)
const playSoundTick = (isSlow = false, isSuspense = false) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    if (isSuspense) {
      // Low heartbeat tension thump
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(90, ctx.currentTime);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.25);
    } else {
      // Crisp mechanical tick
      osc.type = isSlow ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(isSlow ? 220 : 480, ctx.currentTime);
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (isSlow ? 0.1 : 0.03));
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + (isSlow ? 0.12 : 0.04));
    }
  } catch (e) {
    // Audio context may be restricted by browser policy until user gesture
  }
};

const playFanfareCelebration = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    // Auspicious celebratory chime chord: [C5, E5, G5, C6]
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.09);
      gain.gain.setValueAtTime(0.14, ctx.currentTime + idx * 0.09);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.4 + idx * 0.09);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + idx * 0.09);
      osc.stop(ctx.currentTime + 1.5 + idx * 0.09);
    });
  } catch (e) {}
};

const WinnerRevealModal = ({ isOpen, onClose, auction, onRevealComplete }) => {
  const { triggerConfetti } = useCelebration();
  const [displayAmount, setDisplayAmount] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [phaseText, setPhaseText] = useState('Initiating Sacred Count...');
  const [progressPercent, setProgressPercent] = useState(0);

  const animFrameRef = useRef(null);
  const lastSoundTickRef = useRef(0);

  const finalAmount = Number(auction?.winner?.amount || auction?.currentHighestBid || 351);
  const winnerName = auction?.winner?.devoteeName || auction?.currentHighestBidder || 'Devotee Family';
  const offeringTitle = auction?.title || "Maa Durga's Sanctified Offering";
  const category = auction?.category || 'Sacred Item';

  const startRevealAnimation = () => {
    setIsFinished(false);
    setProgressPercent(0);

    const DURATION_MS = 15000; // Exact 15.0-Second Reality Film Sequence
    const startTime = performance.now();
    const target = Math.max(1, finalAmount);

    // Milestone 1 (at 2.5s): Smooth ramp from $1 through single digits
    const milestone1 = Math.max(1, Math.min(target - 1, Math.max(10, Math.floor(target * 0.12))));
    // Milestone 2 (at 10.0s): High-speed cruise ends, starting the movie slowdown
    const milestone2 = Math.max(milestone1, target - Math.max(3, Math.min(22, Math.floor(target * 0.06))));

    setDisplayAmount(1);
    setPhaseText('⚡ Starting Devotional Count: $1, $2, $3...');

    let lastPlayedVal = 1;
    let maxValSoFar = 1;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / DURATION_MS, 1);
      setProgressPercent(Math.floor(progress * 100));

      let currentVal = 1;

      if (elapsed < 2500) {
        // ==========================================
        // SECTION A: 0s to 2.5s (Smooth Progressive Start: 1, 2, 3, 4, 5, 6, 7, 8...)
        // Starts strictly from $1 with progressive single-digit increments
        // ==========================================
        const pA = elapsed / 2500;
        currentVal = Math.max(1, 1 + Math.floor((milestone1 - 1) * Math.pow(pA, 1.4)));
        setPhaseText('⚡ Devotional Count Rising: $1, $2, $3, $4...');

        // Tick on every increment
        if (!isMuted && (currentVal !== lastPlayedVal || currentTime - lastSoundTickRef.current > 120)) {
          playSoundTick(false, false);
          lastSoundTickRef.current = currentTime;
          lastPlayedVal = currentVal;
        }
      } else if (elapsed < 10000) {
        // ==========================================
        // SECTION B: 2.5s to 10s (High-Speed Cruise)
        // Swift continuous roll up to ~92% of target
        // ==========================================
        const pB = (elapsed - 2500) / 7500;
        currentVal = Math.floor(milestone1 + (milestone2 - milestone1) * pB);
        setPhaseText('⚡ Rapid Devotional Bids Spinning Upwards...');

        // Rapid ticks during high-speed cruise
        if (!isMuted && currentTime - lastSoundTickRef.current > 75) {
          playSoundTick(false, false);
          lastSoundTickRef.current = currentTime;
          lastPlayedVal = currentVal;
        }
      } else if (elapsed < 14000) {
        // ==========================================
        // SECTION C: 10s to 14s (The Movie Slowdown)
        // Dramatic ease-out: ticks slow to a crawl!
        // ==========================================
        const pC = (elapsed - 10000) / 4000;
        const justBelowTarget = Math.max(milestone2, target - 1);
        const easedC = 1 - Math.pow(1 - pC, 3);
        currentVal = Math.floor(milestone2 + (justBelowTarget - milestone2) * easedC);

        setPhaseText('🔥 Tension Builds... Approaching Final Highest Bid! 🔥');

        // Slower ticks like reality TV suspense
        const tickInterval = 250 + Math.floor(pC * 500);
        if (!isMuted && (currentVal !== lastPlayedVal || currentTime - lastSoundTickRef.current > tickInterval)) {
          playSoundTick(true, true);
          lastSoundTickRef.current = currentTime;
          lastPlayedVal = currentVal;
        }
      } else if (elapsed < 14800) {
        // ==========================================
        // SECTION D: 14.0s to 14.8s (Suspense Hold)
        // Dead silence hold right on the edge (e.g. $365)!
        // ==========================================
        currentVal = Math.max(1, target - 1);
        setPhaseText('🤫 Heartbeat Pause... Awaiting the Winning Strike! 🤫');

        if (!isMuted && currentTime - lastSoundTickRef.current > 380) {
          playSoundTick(true, true); // Suspense heartbeat thump
          lastSoundTickRef.current = currentTime;
        }
      } else {
        // ==========================================
        // SECTION E: 14.8s to 15.0s (BANG! Final Strike)
        // ==========================================
        currentVal = target;
        setPhaseText('👑 SACRED WINNER REVEALED! 👑');
      }

      // Guarantee strictly monotonic increment (never jumps backward)
      currentVal = Math.max(maxValSoFar, Math.min(target, currentVal));
      maxValSoFar = currentVal;
      setDisplayAmount(currentVal);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        // Reveal Complete!
        setDisplayAmount(target);
        setIsFinished(true);
        if (!isMuted) playFanfareCelebration();
        if (triggerConfetti) triggerConfetti();
        if (onRevealComplete) onRevealComplete();
      }
    };

    animFrameRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (isOpen) {
      startRevealAnimation();
    } else {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isOpen, finalAmount]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl flex flex-col items-center justify-between p-4 sm:p-8 text-white text-center overflow-y-auto animate-in fade-in duration-300">
      {/* Top Bar with Offering info & Controls */}
      <div className="w-full max-w-4xl flex items-center justify-between gap-4 pt-2">
        <div className="flex items-center gap-2 text-left">
          <span className="text-2xl">🌺</span>
          <div>
            <span className="text-[11px] uppercase tracking-widest font-black text-amber-400 block">
              {category} • Official Grand Reveal
            </span>
            <h4 className="text-sm sm:text-base font-bold text-gray-200 line-clamp-1">
              {offeringTitle}
            </h4>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsMuted(!isMuted)}
            className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all text-amber-300"
            title={isMuted ? 'Unmute Sound' : 'Mute Sound'}
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all text-gray-300 hover:text-white"
            title="Close Reveal Stage"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Center Cinematic Stage */}
      <div className="my-auto py-8 w-full max-w-2xl flex flex-col items-center justify-center">
        {/* Sacred Badge */}
        <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs sm:text-sm font-extrabold uppercase tracking-widest mb-6 shadow-[0_0_20px_rgba(251,191,36,0.2)]">
          Maa Durga's Sanctified Auction Climax
        </div>

        {/* Giant Suspense Dollar Spinner */}
        <div className="relative my-4">
          {/* Ambient Glowing Auras */}
          <div className="absolute -inset-8 bg-gradient-to-r from-red-600/30 via-amber-500/40 to-yellow-500/30 blur-3xl rounded-full pointer-events-none animate-pulse" />

          <div className="relative text-7xl sm:text-9xl md:text-[11rem] font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-100 via-amber-300 to-yellow-500 tracking-tight font-mono drop-shadow-[0_0_40px_rgba(251,191,36,0.7)] transition-transform duration-150 transform active:scale-105">
            ${displayAmount}
          </div>
        </div>

        {/* Dynamic Phase Text */}
        <p className="mt-2 text-sm sm:text-lg font-bold tracking-wide transition-all duration-300 text-amber-200">
          {phaseText}
        </p>

        {/* 15-Second Progress Line (during countdown) */}
        {!isFinished && (
          <div className="w-64 sm:w-80 h-1.5 bg-white/15 rounded-full mt-6 overflow-hidden border border-white/10">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-300 rounded-full transition-all duration-100"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {/* WINNER REVEAL CLIMAX CARD (Appears at 15.0s) */}
        {isFinished && (
          <div className="mt-8 w-full bg-gradient-to-br from-amber-950/80 via-red-950/90 to-black/90 border-2 border-amber-400/80 rounded-3xl p-6 sm:p-8 shadow-[0_0_50px_rgba(251,191,36,0.4)] animate-in zoom-in-95 duration-500">
            <div className="flex items-center justify-center gap-2 text-amber-400 font-extrabold text-xs sm:text-sm uppercase tracking-widest mb-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Official Blessed Winner
              <Trophy className="w-5 h-5 text-yellow-400" />
            </div>

            <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-amber-200 to-white drop-shadow">
              {winnerName}
            </h2>

            <div className="mt-3 inline-block bg-amber-400/20 border border-amber-300/40 px-5 py-1.5 rounded-full">
              <span className="text-amber-200 font-bold text-sm sm:text-base">
                Final Sanctified Offering: <strong className="text-white text-lg">${finalAmount}</strong>
              </span>
            </div>

            <p className="mt-4 text-xs sm:text-sm text-amber-100/80 max-w-md mx-auto leading-relaxed">
              May Maa Durga's auspicious grace, peace, and prosperity be forever showered upon your blessed home.
            </p>

            {/* Actions: Replay & Done */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <button
                type="button"
                onClick={startRevealAnimation}
                className="bg-white/15 hover:bg-white/25 text-white border border-white/30 px-5 py-2.5 rounded-2xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all shadow-md transform active:scale-95"
              >
                <RotateCcw className="w-4 h-4 text-amber-300" />
                Replay Suspense Reveal
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-red-950 font-black px-6 py-2.5 rounded-2xl text-xs sm:text-sm shadow-xl transition-all transform active:scale-95"
              >
                View Concluded Offering Page
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Footer Note */}
      <div className="w-full max-w-md pb-2 text-[11px] text-amber-200/60 font-medium">
        Beats of Washington • Navratri Auspicious Sacred Auction
      </div>
    </div>
  );
};

export default WinnerRevealModal;
